/**
 * 设备报警配置 - 表格
 */
import React, { useCallback, useMemo } from 'react';
import { Button, Tag, Modal } from 'antd';
import CommonTable from '@/components/commonTable';
import styles from '@/styles/pageContent.module.scss';
import { displayJtDeviceAlarmV2 } from '@/utils/jtDeviceAlarm';

interface AlarmConfigTableProps {
  data: any[];
  loading: boolean;
  page: number;
  limit: number;
  totalCount: number;
  onPageChange: (page: number, pageSize: number) => void;
  onOpenDetail: (record: any) => void;
  onEdit: (record: any) => void;
  onDelete: (record: any) => void;
  aggregationType: string;
}

const AlarmConfigTable: React.FC<AlarmConfigTableProps> = ({
  data,
  loading,
  page,
  limit,
  totalCount,
  onPageChange,
  onOpenDetail,
  onEdit,
  onDelete,
  aggregationType
}) => {

  // console.log('data', data);

  // 处理删除
  const handleDelete = useCallback((record: any) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条配置吗？',
      okText: '确认',
      cancelText: '取消',
      onOk: () => {
        onDelete(record);
      }
    });
  }, [onDelete]);

  const columns = useMemo(() => {
    if (aggregationType === 'plate') {
      return [
        {
          title: '车辆',
          dataIndex: 'plateNumber',
          key: 'plateNumber',
          width: 150
        },
        {
          title: '报警类型',
          dataIndex: 'alarmTypeList',
          key: 'alarmTypeList',
          width: 400,
          render: (alarmTypeList: any[]) => {
            if (!alarmTypeList || !Array.isArray(alarmTypeList)) return '-';

            // Group by alarmType (1: ADAS, 2: Driver, 3: Blind)
            const grouped: Record<number, any[]> = { 1: [], 2: [], 3: [] };
            alarmTypeList.forEach(item => {
              if (grouped[item.alarmType]) {
                grouped[item.alarmType].push(item);
              }
            });

            // 定义三种报警类型的颜色
            const colorMap: Record<number, string> = {
              1: 'blue',    // ADAS - 蓝色
              2: 'orange',  // Driver - 橙色
              3: 'green'    // Blind - 绿色
            };

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {[1, 2, 3].map(type => {
                  const items = grouped[type];
                  if (items.length === 0) return null;
                  return (
                    <div key={type} style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {items.map((item, index) => (
                        <Tag
                          key={`${item.alarmType}_${item.warningType}_${index}`}
                          color={colorMap[type]} 
                          style={{marginBottom: 4}}
                        >
                          {displayJtDeviceAlarmV2(item)}
                        </Tag>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          }
        },
        {
          title: '操作',
          key: 'action',
          width: 180,
          render: (_: any, record: any) => (
            <div className={styles.operateTextButton}>
              <Button type="link" onClick={() => onOpenDetail(record)}>
                查看详情
              </Button>
              <Button type="link" onClick={() => onEdit(record)}>
                编辑
              </Button>
              <Button type="link" danger onClick={() => handleDelete(record)}>
                删除
              </Button>
            </div>
          )
        }
      ];
    } else if (aggregationType === 'alarmType') {
      return [
        {
          title: '报警类型',
          key: 'alarmType',
          width: 150,
          render: (_: any, record: any) => {
            return displayJtDeviceAlarmV2(record);
          }
        },
        {
          title: '车辆',
          dataIndex: 'plateNumberList',
          key: 'plateNumberList',
          width: 400,
          render: (plateNumberList?: any) => {
            if (!Array.isArray(plateNumberList) || plateNumberList.length === 0) {
              return (<Tag>--</Tag>)
            }
            return plateNumberList.map((p: any) => {
              return (<Tag key={p.id} style={{marginBottom: 4}}>{p.plateNumber}</Tag>)
            })
          }
        },
        {
          title: '操作',
          key: 'action',
          width: 180,
          render: (_: any, record: any) => (
            <div className={styles.operateTextButton}>
              <Button type="link" onClick={() => onOpenDetail(record)}>
                查看详情
              </Button>
              <Button type="link" onClick={() => onEdit(record)}>
                编辑
              </Button>
              <Button type="link" danger onClick={() => handleDelete(record)}>
                删除
              </Button>
            </div>
          )
        }
      ];
    }

    // Default columns (or empty if no aggregation type selected yet)
    return [];
  }, [aggregationType, onOpenDetail, onEdit]);

  const getRowKey = (record: any) => {
    return `${record.alarmType}_${record.warningType}_${record.carId}`;
  }

  return (
    <div className={styles.tableBox}>
      <CommonTable
        columnFilter={false}
        rowKey={getRowKey}
        columns={columns}
        dataSource={data}
        pagination={{
          total: totalCount,
          current: page,
          pageSize: limit,
          onChange: onPageChange
        }}
        bordered
        loading={loading}
      />
    </div>
  );
};

export default AlarmConfigTable;
